import { Outlet, Link, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { HiBars3, HiChevronDown, HiXMark } from "react-icons/hi2";
import {
  FaFacebookF,
  FaInstagram,
  FaLinkedinIn,
  FaYoutube,
  FaLocationDot,
  FaPhone,
  FaEnvelope,
} from "react-icons/fa6";
import logo from "../assets/logo.png";
import logo2 from "../assets/logo2.png";
import ScrollToTopButton from "../components/ScrollToTopButton";

const navSections = [
  {
    id: "about",
    label: "About",
    items: [
      { to: "/philosophy", label: "School Philosophy" },
      { to: "/testimonials", label: "Testimonials" },
    ],
  },
  {
    id: "admissions",
    label: "Admissions",
    items: [
      { to: "/admission-process", label: "Admission Process" },
      { to: "https://forms.gle/m1PLhs8jVYXHs1cWA", label: "Application Form" },
      { to: "/faq", label: "FAQ" },
    ],
  },
  {
    id: "academics",
    label: "Academics",
    items: [
      { to: "/about-nios", label: "About NIOS" },
      { to: "/subjects-offered", label: "Subjects Offered" },
      { to: "/test-preparation", label: "Test Preparation" },
    ],
  },
  {
    id: "counseling",
    label: "Counseling",
    items: [
      { to: "/counseling-office", label: "The Counseling Office" },
      { to: "/career-counseling", label: "Career Counseling" },
      { to: "/career-direction", label: "Career Direction" },
    ],
  },
];

const dropdownPanelClass =
  "absolute left-0 top-full z-50 mt-1.5 w-max min-w-[10rem] rounded-lg border border-brand/12 bg-surface py-1 text-ink shadow-lg";

const dropdownLinkClass =
  "block px-3.5 py-2 text-[0.8125rem] font-medium text-ink/85 transition hover:bg-brand-light hover:text-brand";

function Chevron({ open, className = "" }) {
  return (
    <HiChevronDown
      className={`h-3.5 w-3.5 shrink-0 opacity-60 transition-transform duration-200 ${open ? "rotate-180" : ""} ${className}`}
      aria-hidden
    />
  );
}

function navLinkClass(active) {
  return [
    "rounded-md px-2.5 py-1.5 text-[0.8125rem] font-medium transition",
    active
      ? "bg-brand/10 text-brand"
      : "text-ink/75 hover:bg-brand-light/80 hover:text-brand",
  ].join(" ");
}

function MainLayout() {
  const location = useLocation();
  const pathname = location.pathname;
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSection, setMobileSection] = useState(null);
  const navRef = useRef(null);

  const sectionActive = (section) =>
    section.items.some((item) => pathname === item.to);

  useEffect(() => {
    setOpenDropdown(null);
    setMobileOpen(false);
    setMobileSection(null);
    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpenDropdown(null);
        setMobileOpen(false);
        setMobileSection(null);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!openDropdown && !mobileOpen) return;
    const handlePointerDown = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenDropdown(null);
        setMobileOpen(false);
        setMobileSection(null);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [openDropdown, mobileOpen]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const toggleDropdown = (id) => {
    setOpenDropdown((prev) => (prev === id ? null : id));
  };

  const toggleMobileSection = (id) => {
    setMobileSection((prev) => (prev === id ? null : id));
  };

  return (
    <div className="flex min-h-screen flex-col font-sans">
      <header className="sticky top-0 z-40 border-b border-brand/15 bg-surface/95 backdrop-blur-sm">
        <div ref={navRef} className="relative flex flex-col">
          <div className="order-1 bg-brand px-2.5 py-1 text-[0.7rem] text-white sm:px-4 sm:py-1.5 sm:text-[0.75rem] md:px-6 lg:px-8">
            <div className="flex flex-col items-center gap-1 text-center sm:gap-1.5 lg:flex-row lg:items-center lg:justify-between lg:text-left">
              <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-0.5 sm:gap-x-4 sm:gap-y-1 lg:flex-row lg:items-center lg:justify-start">
                <a href="tel:+919867480644" className="hover:text-white/85">
                  +91 9867480688
                </a>
                <a href="mailto:info@altusonlineschool.com" className="hover:text-white/85">
                  info@altusonlineschool.com
                </a>
              </div>
              <div className="flex items-center justify-center gap-2 sm:gap-3">
                <a
                  href="https://www.facebook.com/share/1EASAAsYjN/?mibextid=wwXIfr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/15 text-white transition hover:bg-white/25 sm:h-7 sm:w-7"
                  aria-label="Facebook"
                >
                  <FaFacebookF className="h-3 w-3 sm:h-3.5 sm:w-3.5" aria-hidden />
                </a>
                <a
                  href="https://www.instagram.com/altusonlineschool?igsh=b243dTFuOGZhdzh4"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/15 text-white transition hover:bg-white/25 sm:h-7 sm:w-7"
                  aria-label="Instagram"
                >
                  <FaInstagram className="h-3 w-3 sm:h-3.5 sm:w-3.5" aria-hidden />
                </a>
                <a
                  href="https://linkedin.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/15 text-white transition hover:bg-white/25 sm:h-7 sm:w-7"
                  aria-label="LinkedIn"
                >
                  <FaLinkedinIn className="h-3 w-3 sm:h-3.5 sm:w-3.5" aria-hidden />
                </a>
                <a
                  href="https://youtube.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/15 text-white transition hover:bg-white/25 sm:h-7 sm:w-7"
                  aria-label="YouTube"
                >
                  <FaYoutube className="h-3 w-3 sm:h-3.5 sm:w-3.5" aria-hidden />
                </a>
              </div>
              <p className="text-sm font-bold uppercase tracking-[0.05em] text-white sm:mt-0.5 sm:text-base sm:tracking-[0.06em] lg:hidden">
                Altus Online School
              </p>
            </div>
          </div>
          <div className="order-2 flex w-full min-w-0 items-center justify-between gap-3 py-2.5 sm:gap-4 sm:py-3">
            <Link
              to="/"
              className="flex min-w-0 shrink-0 items-center pl-3 no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:ring-offset-2 sm:pl-4 md:pl-6 lg:pl-8"
            >
              <img
                src={logo}
                alt="Altus Online School — Education, Personalised"
                className="h-24 w-auto max-h-[6.5rem] object-contain object-left sm:h-14 md:h-[4.25rem] lg:h-16 lg:max-h-[4.75rem] max-w-[min(100%,calc(100vw-5.5rem))] sm:max-w-[min(100%,26rem)] md:max-w-[min(100%,30rem)]"
                decoding="async"
              />
            </Link>

            <nav
              className="hidden shrink-0 items-center gap-0.5 pr-3 sm:pr-4 md:pr-6 lg:flex lg:pr-8"
              aria-label="Primary"
            >
              <Link to="/" className={navLinkClass(pathname === "/")}>
                Home
              </Link>

              {navSections.map((section) => {
                const active = sectionActive(section);
                return (
                  <div key={section.id} className="relative">
                    <button
                      type="button"
                      className={[
                        "flex items-center gap-0.5 rounded-md px-2.5 py-1.5 text-[0.8125rem] font-medium transition",
                        active || openDropdown === section.id
                          ? "bg-brand/10 text-brand"
                          : "text-ink/75 hover:bg-brand-light/80 hover:text-brand",
                      ].join(" ")}
                      aria-expanded={openDropdown === section.id}
                      aria-haspopup="true"
                      onClick={() => toggleDropdown(section.id)}
                    >
                      {section.label}
                      <Chevron open={openDropdown === section.id} />
                    </button>
                    {openDropdown === section.id && (
                      <div className={dropdownPanelClass} role="menu">
                        {section.items.map((item) => (
                          <Link
                            key={item.to}
                            to={item.to}
                            role="menuitem"
                            className={dropdownLinkClass}
                            onClick={() => setOpenDropdown(null)}
                          >
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

              <Link to="/contact" className={navLinkClass(pathname === "/contact")}>
                Contact
              </Link>
            </nav>

            <button
              type="button"
              className="inline-flex shrink-0 items-center justify-center rounded-md p-2 pr-3 text-brand sm:pr-4 md:pr-6 lg:hidden lg:pr-8"
              aria-expanded={mobileOpen}
              aria-controls="mobile-nav"
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              onClick={() => setMobileOpen((o) => !o)}
            >
              {mobileOpen ? (
                <HiXMark className="h-6 w-6" aria-hidden />
              ) : (
                <HiBars3 className="h-6 w-6" aria-hidden />
              )}
            </button>
          </div>

          {mobileOpen && (
            <div
              id="mobile-nav"
              className="absolute left-0 right-0 top-full z-50 border-t border-brand/10 bg-surface py-3 pl-3 pr-4 shadow-xl sm:pl-4 sm:pr-5 md:pl-6 md:pr-6 lg:hidden"
            >
              <div className="flex flex-col gap-0.5">
                <Link
                  to="/"
                  className={navLinkClass(pathname === "/") + " py-2.5"}
                  onClick={() => setMobileOpen(false)}
                >
                  Home
                </Link>
                {navSections.map((section) => (
                  <div key={section.id} className="border-t border-brand/10 pt-1">
                    <button
                      type="button"
                      className="flex w-full items-center justify-between rounded-md px-2.5 py-2.5 text-left text-[0.9375rem] font-medium text-ink/80 hover:bg-brand-light/70"
                      aria-expanded={mobileSection === section.id}
                      onClick={() => toggleMobileSection(section.id)}
                    >
                      {section.label}
                      <Chevron open={mobileSection === section.id} />
                    </button>
                    {mobileSection === section.id && (
                      <div className="pb-2 pl-1">
                        {section.items.map((item) => (
                          <Link
                            key={item.to}
                            to={item.to}
                            className="block rounded-md px-3 py-2 text-sm text-ink/80 hover:bg-brand-light hover:text-brand"
                            onClick={() => setMobileOpen(false)}
                          >
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                <Link
                  to="/contact"
                  className={navLinkClass(pathname === "/contact") + " py-2.5"}
                  onClick={() => setMobileOpen(false)}
                >
                  Contact
                </Link>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* NO CONTAINER HERE - Let each page decide its own layout */}
      <main className="flex-1">
        <Outlet />
      </main>

      <ScrollToTopButton />

      <footer className="mt-auto bg-gradient-to-r from-brand to-brand-dark text-white">
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
          <div className="grid gap-8 border-b border-brand-accent/35 pb-8 text-center md:grid-cols-2 md:text-left lg:grid-cols-[1.2fr_1fr_1fr_1fr_1fr]">
            <div>
              <img
                src={logo2}
                alt="Altus Online School — Education, Personalised"
                className="mx-auto h-auto w-full max-w-[220px] object-contain md:mx-0"
                decoding="async"
              />

              <ul className="mt-5 space-y-3 text-sm text-brand-light/90">
                <li className="flex items-start justify-center gap-3 text-left md:justify-start">
                  <FaLocationDot className="mt-0.5 h-4 w-4 shrink-0 text-brand-accent" aria-hidden />
                  <span>
                    Raheja Classic, new Link Road 
                    <br />
                    Oshiwara next to infinity Mall
                    <br />
                    Andheri West Mumbai
                  </span>
                </li>
                <li className="flex items-center justify-center gap-3 md:justify-start">
                  <FaEnvelope className="h-4 w-4 shrink-0 text-brand-accent" aria-hidden />
                  <a href="mailto:info@altusonlineschool.com" className="hover:text-white">
                    info@altusonlineschool.com
                  </a>
                </li>
                <li className="flex items-center justify-center gap-3 md:justify-start">
                  <FaPhone className="h-4 w-4 shrink-0 text-brand-accent" aria-hidden />
                  <a href="tel:+919867480644" className="hover:text-white">
                    +91 9867480688
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-white">Quick Link</h3>
              <nav className="mt-4 space-y-2.5 text-sm" aria-label="Company">
                <Link to="/philosophy" className="block text-brand-light/90 transition hover:text-white">
                  School Philosophy
                </Link>
                {/* <Link to="/testimonials" className="block text-brand-light/90 transition hover:text-white">
                  Testimonials
                </Link> */}
                <Link to="/admission-process" className="block text-brand-light/90 transition hover:text-white">
                  Admissions
                </Link>
                <Link to="/career-direction" className="block text-brand-light/90 transition hover:text-white">
                  Career Pathway
                </Link>
              </nav>
            </div>

            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-white">Academics</h3>
              <nav className="mt-4 space-y-2.5 text-sm" aria-label="Academics">
                <Link to="/about-nios" className="block text-brand-light/90 transition hover:text-white">
                  About NIOS
                </Link>
                <Link to="/subjects-offered" className="block text-brand-light/90 transition hover:text-white">
                  Subjects Offered
                </Link>
                <Link to="/test-preparation" className="block text-brand-light/90 transition hover:text-white">
                  Exam Preparation
                </Link>
              </nav>
            </div>

            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-white">Support</h3>
              <nav className="mt-4 space-y-2.5 text-sm" aria-label="Support">
                <Link to="/testimonials" className="block text-brand-light/90 transition hover:text-white">
                  Student Stories
                </Link>
                <Link to="/faq" className="block text-brand-light/90 transition hover:text-white">
                  Help Center
                </Link>
              </nav>
            </div>

            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-white">Important Links</h3>
              <nav className="mt-4 space-y-2.5 text-sm" aria-label="Important Links">
                <Link to="https://forms.gle/m1PLhs8jVYXHs1cWA" className="block text-brand-light/90 transition hover:text-white">
                  Application Form
                </Link>
                <Link to="/contact" className="block text-brand-light/90 transition hover:text-white">
                  Contact
                </Link>
                <Link to="/counseling-office" className="block text-brand-light/90 transition hover:text-white">
                  Counseling Office
                </Link>
              </nav>
            </div>

          </div>

          <div className="grid gap-4 pt-6 text-xs text-brand-light/80 sm:grid-cols-3 sm:items-center">
            <p className="text-center sm:text-left">
              &copy; {new Date().getFullYear()}{" "}
              <a
                href="https://100billiontech.com/ownership"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-white transition hover:text-brand-light"
              >
                Altus Online School Copy-rights reserved.
              </a>{" "}
              Designed by{" "}
              <a
                href="https://100billiontech.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-white transition hover:text-brand-light"
              >
                100billion Tech
              </a>
            </p>

            <div className="flex items-center justify-center gap-2.5">
              <a
                href="https://www.facebook.com/share/1EASAAsYjN/?mibextid=wwXIfr"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-brand-accent/25 text-white transition hover:bg-brand-accent/40"
                aria-label="Facebook"
              >
                <FaFacebookF className="h-3.5 w-3.5" aria-hidden />
              </a>
              <a
                href="https://www.instagram.com/altusonlineschool?igsh=b243dTFuOGZhdzh4"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-brand-accent/25 text-white transition hover:bg-brand-accent/40"
                aria-label="Instagram"
              >
                <FaInstagram className="h-3.5 w-3.5" aria-hidden />
              </a>
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-brand-accent/25 text-white transition hover:bg-brand-accent/40"
                aria-label="LinkedIn"
              >
                <FaLinkedinIn className="h-3.5 w-3.5" aria-hidden />
              </a>
              <a
                href="https://youtube.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-brand-accent/25 text-white transition hover:bg-brand-accent/40"
                aria-label="YouTube"
              >
                <FaYoutube className="h-3.5 w-3.5" aria-hidden />
              </a>
            </div>

            <nav className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 sm:justify-end" aria-label="Legal">
              <Link to="/disclaimer" className="transition hover:text-white">
                Disclaimer
              </Link>
              <Link to="/terms-conditions" className="transition hover:text-white">
                Terms & Conditions
              </Link>
              <Link to="/privacy-policy" className="transition hover:text-white">
                Privacy Policy
              </Link>
            </nav>
          </div>

        </div>
      </footer>
    </div>
  );
}

export default MainLayout;